
import { ProvincesShortCode, lddGlobalSessionInfo } from "../navigationTypes";

export interface FileConditionsInterface{
    conditions: Array<TFileConditionyDate>
}

export type TFileConditionyDate = {
    id: number,
    title?: string,
    psm?: string,
    xpathFunc: string,
    province?: ProvincesShortCode[]
}

namespace FileCondition {

    function toBoolean(value: unknown): boolean {

        if (typeof value === "boolean") {
            return value;
        }
        if (typeof value === "string") {
            return value.length > 0;
        }
        if (typeof value === "number") {
            return value > 0;
        }
        return false;
    }

    function provinceMatches(
        conditionProvinces: ProvincesShortCode[] | undefined,
        currentProvince: ProvincesShortCode
    ): boolean {
        // Condition does not require province filter: only pass if the file actually has province info.
        if (!conditionProvinces || conditionProvinces.length === 0) {
            return !!currentProvince;
        }
        return conditionProvinces.includes(currentProvince);
    }

    function psmMatches(mask: string | undefined, current: string | undefined): boolean {
        const normalizedMask = (mask || "").toUpperCase().replace(/\s+/g, "");
        const normalizedCurrent = (current || "").toUpperCase().replace(/\s+/g, "");

        // If either the condition doesn't specify a PSM mask or the file has no PSM value, the PSM test should fail.
        if (!normalizedCurrent) {
            return false;
        }
        else if ( normalizedMask.length === 0) { // no fitler on PSM 
            return true;
        }
        else { 
            return normalizedCurrent.split("").some((ch) => normalizedMask.includes(ch));
        }
    }

    // Convert DOM Element to ajaxslt XDocument
    function getTpAppXMLNode(dataRoot: Element): any | null {
        const maybeXNode = dataRoot as unknown as { runXpath?: (xpath: string) => unknown };
        if (typeof maybeXNode.runXpath === "function") {
            return maybeXNode as unknown as ajaxslt.XNode;
        }

        try {
          
            let xmlString: string | undefined;
            const anyRoot = dataRoot as any;

            if (anyRoot.ownerDocument && typeof anyRoot.ownerDocument.xml === "string") {
                xmlString = anyRoot.ownerDocument.xml;
            } else {
                const serializer = new XMLSerializer();
                xmlString = serializer.serializeToString(
                    anyRoot.ownerDocument ? anyRoot.ownerDocument : dataRoot
                );
            }

            const xmlDoc = new (window as any).ajaxslt.XDocument();
            xmlDoc.loadXML(xmlString);

            const tpNode =
                (xmlDoc.selectSingleNode &&
                    xmlDoc.selectSingleNode("//TPAppXML")) ||
                xmlDoc.selectSingleNode("TPAppXML");
            return tpNode || xmlDoc.documentElement;
        } catch (e) {
            console.error("FileCondition.getTpAppXMLNode: failed to create TPAppXML node", e);
            return null;
        }
    }

    function conditionMatchesContext( // province and PSM filter using XML and string matching
        condition: TFileConditionyDate,
        normalizedProvince: ProvincesShortCode,
        tpAppXMLNode: any
    ): boolean {
        // Business rule: if the file is missing *either* province or PSM info,
        if (!normalizedProvince) {
            return false;
        }

        const rawPsm = (tpAppXMLNode as any).runXpath("string(@PSM)") as string | null | undefined;
        const xmlPsm = String(rawPsm || "").toUpperCase();

        if (!xmlPsm) {
            return false;
        }

        // If a PSM mask is defined on the condition, enforce it.
        // If not, ignore the mask and only rely on province.
        const psmOk = !condition.psm ? true : psmMatches(condition.psm, xmlPsm);
        const provinceOk = provinceMatches(condition.province, normalizedProvince);
        return psmOk && provinceOk;
    }

    // Evaluate a **single** condition's XPath against the TPAppXML node.
    function evaluateSingleXPathCondition(
        tpAppXMLNode: any,
        condition: TFileConditionyDate
    ): { id: number; title: string; value: boolean } | null {
        try {
            const rawResult = (tpAppXMLNode as any).runXpath(condition.xpathFunc);
            const value = toBoolean(rawResult);

            if (!value) {
                return null;
            }
            return {
                id: condition.id,
                title: condition.title || "",
                value: true,
            };
        } catch (e) {
            console.error(
                "FileCondition.evaluateSingleCondition: error evaluating xpath",
                condition.xpathFunc,
                e.message
            );
            return null;
        }
    }

    // Iteration on test case against User file conditions.
    export const evaluateFileConditions = (
        dataRoot: Element,
        province: ProvincesShortCode,
        conditions: TFileConditionyDate[] = FileConditionsDefine.conditions
    ): Array<{ id: number; title: string; value: boolean }> => {
        try {
            const tpAppXMLNode = getTpAppXMLNode(dataRoot);
            if (!tpAppXMLNode || typeof (tpAppXMLNode as any).runXpath !== "function") {
                console.error(
                    "FileCondition.evaluateFileConditions: TPAppXML node does not support runXpath"
                );
                return [];
            }
            const results: Array<{ id: number; title: string; value: boolean }> = [];

            for (const condition of conditions) {
                const include = conditionMatchesContext(
                    condition,
                    province,
                    tpAppXMLNode
                );
                // Only proceed to XPath evaluation if province / PSM context matches
                if (!include) {
                    continue;
                }
                const evaluated = evaluateSingleXPathCondition(tpAppXMLNode, condition);
                if (evaluated) {
                    results.push(evaluated);
                }
            }
            return results;
        } catch (e) {
            // Catastrophic failure: return empty array
            console.error("FileCondition.evaluateFileConditions: catastrophic failure", e);
            return [];
        }
    };

    export const LoadInFileConditionList = async (AppID: string, SESSION_INFO: lddGlobalSessionInfo): Promise<any[]> => {
        return fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO?.appProvinceShort}/${SESSION_INFO?.firm.id}/${AppID}/fileconditions/?SessionID=${SESSION_INFO?.sid}`)
            .then(async (response) => {
                if (response.status !== 200) {
                    return [];
                } else {
                    const data = await response.json();
                    return data.result;
                }
            })
    }

    export const SaveFileConditions = async (
        appId: string,
        conditions: Array<{ id: number; title: string; value: boolean }>,
        SESSION_INFO: lddGlobalSessionInfo
    ): Promise<void> => {
        const province = SESSION_INFO?.appProvinceShort;
        const firmId = SESSION_INFO?.firm?.id;
        const sid = SESSION_INFO?.sid;

        if (!province || !firmId || !sid) {
            console.error("SaveFileConditions: missing province/firmId/sid in SESSION_INFO");
            return;
        }
    
        const url = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${province}/${firmId}/save/fileconditions?SessionID=${sid}`;
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                // Send evaluated conditions for this file so the backend can persist them
                body: JSON.stringify({
                    appId,
                    conditions,
                }),
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(
                    `SaveFileConditions failed: ${response.status} ${response.statusText} ${text}`
                );
            }
        } catch (e) {
            console.error("SaveFileConditions: error saving file conditions", e);
            throw e;
        }
    };

    export const FileConditionsDefine: FileConditionsInterface = {
        conditions:[   
            {
                    id: 1,
                    title: "Is New Home",           
                    psm: "PS",
                    xpathFunc:"@NewHome='0'"   
                },
                {
                    id: 2,
                    title: "Is Condo",
                    xpathFunc:"@PropertyType='Condo'"
                },
                {
                    id: 3,
                    title: "Is Freehold",
                    xpathFunc:"@PropertyType='Freehold (Fee Simple)'",
                },
                {
                    id: 4,
                    title: "Is Leasehold",
                    xpathFunc:"contains(@PropertyType,'Leasehold')",
                    province: ["BC", "AB"]
                },
                {
                    id: 5,
                    title: "Is Farm Land",
                    xpathFunc:"@Farmland='1'or @FarmLand='1'",
                    province: ["AB", "MB", "SK"]
                },
                {
                    id: 6,
                    title: "Is Vacant Land",
                    psm: "PS",
                    xpathFunc: "@VacantLand='1'" , 
                    province: ["SK"]
                },
                {
                    id: 6,
                    title: "Is Vacant Land",
                    psm: "PS",
                    xpathFunc: "@PropertyQuestion='2' and @NewHome='1'",
                    province: ["ON", "BC"]
                },
                {
                    id: 6,
                    title: "Is Vacant Land",
                    psm: "PS",
                    xpathFunc: "@PropertyQuestion='2'",
                    province: ["AB", "MB"]
                },
                {
                    id: 8,
                    title: "Has Extra Properties",
                    xpathFunc:"count(Property/Property) > 0",
                    province: ["BC"]
                },
                {
                    id: 9,
                    title: "Is Commercial Property",
                    xpathFunc:"@CommercialBundle='1'",
                    province: ["ON"]
                },
                {
                    id: 10,
                    title: "Is Title Transfer Deal",
                    xpathFunc:"@PSMDummy='T'",
                    province: ["BC"]
                },
                {
                    id: 11,
                    title: "Is Cash Deal",
                    xpathFunc:"false()",
                    province: ["BC"]
                },
                {
                    id: 12,
                    title: "Has New Mortgage",
                    psm: "P",
                    xpathFunc:"count(Mortgage[not(not(@Lender)) and not(@Lender='') and not(@IsVTB='1')]) > 0"
                },
                {
                    id: 13,
                    title: "Has Mortgage to Discharge",
                    xpathFunc:"count(Defect[@type='32_2'])>0"
                },
                {
                    id: 14,
                    title: "Has Private Mortgage",
                    psm: "P",
                    xpathFunc:"count(Mortgage[not(not(@Lender)) and not(@Lender='') and not(@IsVTB='1') and @PrivateLender='1']) > 0"
                },
                {
                    id: 15,
                    title: "Has Title Insurance",
                    xpathFunc:"not(not(@InsuranceProviderNew)) and not(@InsuranceProviderNew='') and not(@InsuranceProviderNew='N/A')",
                },
                {
                    id: 16,
                    title: "Has Active Client Portal",
                    xpathFunc:"@ClientConnectActive='true'"
                },
                {
                    id: 17,
                    title: "Acting on Both Sides",
                    psm: "PS",
                    xpathFunc:"@ActingBoth='1' or (not(not(@LinkedAppID)) and not(@LinkedAppID=''))",
                    province: ["AB", "BC", "MB", "SK", "NS"]
                }, 
                {
                    id: 18,
                    title: "Is First Nation Land",
                    xpathFunc:"@IsIndianLandHome='1'",
                    province: ["BC"]
                }, 
                {
                    id: 19,
                    title: "Is Manufactured Home",
                    xpathFunc:"@IsManufacturedHome='1'",
                    province: ["BC", "MB", "SK"]
                }, 
                {
                    id: 20,
                    title: "Has Open Permits",
                    xpathFunc:"@OpenPermits='1'",
                    province: ["MB"]
                },
                {
                    id: 21,
                    title:"Is Acreage",
                    xpathFunc:"@Acreage='1'",
                    province: ["SK"]
                },
                {
                    id: 22,
                    title: "Is Multi Parcel",
                    xpathFunc:"count(LegalDescriptionInput) > 1",
                    province: ["AB"]
                },
                {
                    id: 22,
                    title: "Is Multi Parcel",
                    xpathFunc:"count(LegalDescriptionInput[not(not(@ParcelNum)) and not(@ParcelNum='')]) > 1",
                    province: ["MB", "SK"]
                },
                {
                    id: 25,
                    title: "Discharge has Undertaking not Complied With",
                    xpathFunc:"count(Defect[@type='32_2' and @Undertaking='1']/Data[not(@DateCompliedWith) or @DateCompliedWith=''])>0"
                }
            ]       
    }
}

export default FileCondition;